import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Modal, Button } from 'react-bootstrap';
import i18next from "i18next";
import { setUserInfo } from '../../../reducers/userReducers.jsx';
import { clearLangCodeState } from '../../../reducers/langCodeReducers.jsx';
import { setLangCodeInfo } from '../../../reducers/langCodeReducers.jsx';
import API from '../../../api/api.jsx';

// 계정정보 모달
const Profile_001 = ({ showModal, setShowModal, userInfo }) => {
    const dispatch                                            = useDispatch();
    const { t, i18n }                                         = useTranslation();
    const [isPasswordChangeChecked, setPasswordChangeChecked] = useState(false);
    const [selectOptions, setSelectOptions]                   = useState([]);
    const [passwordVisible, setPasswordVisible]               = useState(false);
    const [newPasswordVisible, setNewPasswordVisible]         = useState(false);
    const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);

    // 현재 비밀번호
    const togglePasswordVisibility = () => {
        setPasswordVisible(!passwordVisible);
    };

    // 신규 비밀번호
    const toggleNewPasswordVisibility = () => {
        setNewPasswordVisible(!newPasswordVisible);
    };

    // 신규 비밀번호 확인
    const toggleConfirmPasswordVisibility = () => {
        setConfirmPasswordVisible(!confirmPasswordVisible);
    };

    // staffInfo 상태값 초기화
	const initialStaffInfo = {
		user_id          : userInfo.user_id,
		tel_number       : userInfo.tel_number,
		phone_number     : userInfo.phone_number,
		email            : userInfo.email,
        lang_code        : userInfo.lang_code,
        password         : '',
        new_password     : '',
        confirm_password : '',
	};

	// 직원 정보
	const [staffInfo, setStaffInfo] = useState(initialStaffInfo);

    // TextInput
    const TextInput = ({ type = 'text', className, value, readOnly, onChange }) => (
        <input type={type} className={`form-control ${className}`} value={value} readOnly={readOnly} onChange={onChange} />
    );

    // PasswordInput
    const PasswordInput = ({ type = 'password', className, value, readOnly, onChange }) => (
        <input type={type} className={`form-control ${className}`} value={value} readOnly={readOnly} onChange={onChange} />
    );

    // 다국어
    const SelectInput = ({ options, value, onChange, t }) => (
        <select
            className='form-select'
            onChange={onChange}
            value={value}
        >
            {options.map((option, index) => (
                <option key={index} value={option[0]}>
                    {t(`${option[1]}.${option[0]}`, { defaultValue: `${option[0]}` })}
                </option>
            ))}
        </select>
    );

    // 공통적인 요소 렌더링
    const renderTextInput = useMemo(() => (labelKey, position = '', type, value = "", field = '', attr = false, edit = false) => (
        <>
            <div className='col-md-2 bg-light d-flex align-items-center pt-2 pb-2'>{labelKey}</div>
            <div className={`col-md-4 pt-2 pb-2 ${position}`}>
                {/* 유저 정보에서 다국어가 필요한 것은 권한 뿐이라 아래와 같이 적용을 해도 큰 문제 X */}
                <TextInput type={type} value={t(`AUTHORITY.${value}`, { defaultValue: value })} readOnly={attr} onChange={(e) => handleInputChange(field, e.target.value)} />
                {edit && (
                    <div className="input-group-prepend position-absolute top-0 end-0 bottom-0 d-flex">
                        <span className="input-group-text bg-none border-0 pe-3">
                            <i className="fa-solid fa-pencil"></i>
                        </span>
                    </div>
                )}
            </div>
        </>
    ), []);

    const renderPasswordInput = useMemo(() => (labelKey, position = '', type, value  = "", field = '', attr = false, edit = false, onToggleVisibility, isVisible) => (
        <>
            <div className='col-md-2 bg-light d-flex align-items-center pt-2 pb-2'>{labelKey} <span className="text-danger">*</span></div>
            <div className={`col-md-4 pt-2 pb-2 ${position}`}>
                {isVisible ? (
                    <PasswordInput type='text' value ={value} readOnly={attr} onChange={(e) => handleInputChange(field, e.target.value)} isVisible={isVisible} />
                ) : (
                    <PasswordInput type={type} value ={value} readOnly={attr} onChange={(e) => handleInputChange(field, e.target.value)} isVisible={isVisible} />
                )}
                {edit && (
                    <div className="input-group-prepend position-absolute top-0 end-0 bottom-0 d-flex">
                        <Button className="input-group-text bg-none border-0 pe-3" onClick={onToggleVisibility}>
                            {isVisible ? (
                                <i className="fa-solid fa-eye-slash"></i>
                            ) : (
                                <i className="fa-solid fa-eye"></i>
                            )}
                        </Button>
                    </div>
                )}
            </div>
        </>
    ), [passwordVisible, newPasswordVisible, confirmPasswordVisible]);

    // 입력 필드 값 변경 핸들러
	const handleInputChange = useCallback((field, value) => {
		setStaffInfo((prevInfo) => ({
		...prevInfo,
		[field]: value,
		}));
	}, [setStaffInfo]);

    // 다국어 변경
    const handleChangeSelect = (e) => {
        handleInputChange('lang_code', e.target.value);
	};

    // 다국어 변경 후 수정
    const updateUserInfoAndLangCode = (langCode) => {
        dispatch(clearLangCodeState());

        const updatedUserInfo = {
            ...userInfo,
            tel_number   : staffInfo.tel_number,
            phone_number : staffInfo.phone_number,
            email        : staffInfo.email,
            lang_code    : langCode,
        };

        dispatch(setUserInfo(updatedUserInfo));
        dispatch(setLangCodeInfo(langCode));

        localStorage.setItem('langCodeInfo', JSON.stringify(langCode));

        const langCodeStorage = JSON.parse(localStorage.getItem('langCodeInfo'));

        if (langCodeStorage) {
            i18next.changeLanguage(langCode);
        }
    };

    // 이메일 정규표현식
    const isEmailValid = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    // 수정 버튼 액션
    const handleEditData = () => {
        const profilePost = async () => {
			try {
				const data     = await API.editProfile({staffInfo});

                const langCode = data.content.lang_code;

                dispatch(setUserInfo({
                    ...userInfo,
                    tel_number   : staffInfo.tel_number,
                    phone_number : staffInfo.phone_number,
                    email        : staffInfo.email,
                }));

                // 언어가 변경된 경우
                if (userInfo.lang_code !== langCode) {
                    updateUserInfoAndLangCode(langCode);
                }

                alert('정상적으로 수정되었습니다.');

                handleClose();

			} catch (error) {
				console.error('Error Edit Profile data:', error);
                
                // 현재 비밀번호가 틀린 경우
                if (error.response.status === 401) {
                    alert('비밀번호를 다시 한번 확인해주세요.');
                    return;
                };

                // 500 에러
                if (error.response.status === 500) {
                    alert('오류가 발생했습니다. 관리자에게 문의하세요');
                    return;
                };
			}
		};

        // 이메일 정규식
        if (!isEmailValid(staffInfo.email)) {
            alert('올바른 이메일 형태를 입력해주세요.');
            return;
        };

        // 비밀번호변경이 열린 경우 체크 루틴
        if (isPasswordChangeChecked) {

            if (!staffInfo.password) {
                alert('현재 비밀번호를 입력해주세요.');
                return;
            }

            if (!staffInfo.new_password) {
                alert('신규 비밀번호를 입력해주세요.');
                return;
            }

            if (!staffInfo.confirm_password) {
                alert('신규 비밀번호 확인을 입력해주세요.');
                return;
            }
            
            if (staffInfo.new_password !== staffInfo.confirm_password) {
                alert('신규 비밀번호를 다시 한번 확인해주세요.');
                return;
            }

        };

        profilePost();
    };

    // 화면 접근 시 다국어 목록 불러오기
    useEffect(() => {     
        const fetchBaseOptions = async () => {
			try {
				const data     = await API.fetchBaseOptions();

				const options  = data.filter(item => item.code_nick_name === '다국어');
				const result   = options.map(item => [item.code,item.code_se]);

				setSelectOptions(result);
                
			} catch (error) {
				console.error('Error fetching Base Options data:', error);
			}
		};

        if (showModal) {     
            fetchBaseOptions();
        }

    }, [showModal, t, i18n.language]);

    // 모달 닫기
    const handleClose = () => {		
        setShowModal(false);
	};

    // 비밀번호 변경 체크박스
    const handlePasswordChangeCheckbox = (e) => {
        staffInfo.password         = '';
        staffInfo.new_password     = '';
        staffInfo.confirm_password = '';

        setPasswordVisible(false);
        setNewPasswordVisible(false);
        setConfirmPasswordVisible(false);

        setPasswordChangeChecked(e.target.checked);
    };

    return (
        <Modal show={showModal} onHide={handleClose} centered dialogClassName="modal-lg" backdrop="static">
            <Modal.Header closeButton>
            <Modal.Title>{t(`ACCOUNT_INFO.계정정보`, { defaultValue: '계정정보' })}</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <div className='row ms-0'>
                    {renderTextInput(t(`STAFF_MANAGEMENT.아이디`, { defaultValue: '아이디' }), '', 'text', userInfo.user_id, '', 'true', '')}
                    {renderTextInput(t(`STAFF_MANAGEMENT.이름`, { defaultValue: '이름' }), '', 'text', userInfo.name, '', 'true', '')}
                    {renderTextInput(t(`STAFF_MANAGEMENT.지점`, { defaultValue: '지점' }), '', 'text', userInfo.branch_name, '', 'true', '')}
                    {renderTextInput(t(`STAFF_MANAGEMENT.권한`, { defaultValue: '권한' }), '', 'text', userInfo.authority, '', 'true', '')}
                    {renderTextInput(t(`ACCOUNT_INFORMATION.전화번호`, { defaultValue: '전화번호' }), 'position-relative', 'text', staffInfo.tel_number, 'tel_number', '', 'true')}
                    {renderTextInput(t(`ACCOUNT_INFORMATION.휴대폰번호`, { defaultValue: '휴대폰번호' }), 'position-relative', 'text', staffInfo.phone_number, 'phone_number', '', 'true')}
                    {renderTextInput(t(`ACCOUNT_INFORMATION.이메일주소`, { defaultValue: '이메일주소' }), 'position-relative', 'text', staffInfo.email, 'email', '', 'true')}
                    {/* {renderTextInput(t(`ACCOUNT_INFO.다국어`, { defaultValue: '다국어' }), '', 'text', staffInfo.lang_code, '', 'true', '')} */}
                    <div className='col-md-2 bg-light d-flex align-items-center pt-2 pb-2'>{t(`ACCOUNT_INFO.다국어`, { defaultValue: '다국어' })}</div>
                    <div className='col-md-4 pt-2 pb-2'>
                        <SelectInput value={staffInfo.lang_code} options={selectOptions} onChange={handleChangeSelect} t={t} />
                    </div>

                    {/* 비밀번호변경 */}
                    <div className='col-md-2 bg-light d-flex align-items-center pt-2 pb-2'>{t(`ACCOUNT_INFORMATION.비밀번호변경`, { defaultValue: '비밀번호변경' })}</div>
                    <div className='col-md-4 pt-2 pb-2'>
                        <input type='checkbox' onChange={handlePasswordChangeCheckbox} /> {t(`ACCOUNT_INFORMATION.비밀번호변경`, { defaultValue: '비밀번호변경' })}
                    </div>

                    {/* 추가적인 컬럼들 (비밀번호 변경이 체크된 경우에만 표시) */}
                    {isPasswordChangeChecked && (
                        <>
                            <div className='col-md-2 d-flex align-items-center pt-2 pb-2'></div>
                            <div className='col-md-4 pt-2 pb-2'></div>

                            {renderPasswordInput(t(`ACCOUNT_INFO.현재 비밀번호`, { defaultValue: '현재 비밀번호' }), 'position-relative', 'password', staffInfo.password, 'password', '', 'true', togglePasswordVisibility, passwordVisible)}

                            <div className='col-md-2 d-flex align-items-center pt-2 pb-2'></div>
                            <div className='col-md-4 pt-2 pb-2'></div>

                            {renderPasswordInput(t(`ACCOUNT_INFO.신규 비밀번호`, { defaultValue: '신규 비밀번호' }), 'position-relative', 'password', staffInfo.new_password, 'new_password', '', 'true', toggleNewPasswordVisibility, newPasswordVisible)}

                            <div className='col-md-2 d-flex align-items-center pt-2 pb-2'></div>
                            <div className='col-md-4 pt-2 pb-2'></div>

                            {renderPasswordInput(t(`ACCOUNT_INFO.신규 비밀번호 확인`, { defaultValue: '신규 비밀번호 확인' }), 'position-relative', 'password', staffInfo.confirm_password, 'confirm_password', '', 'true', toggleConfirmPasswordVisibility, confirmPasswordVisible)}
                        </>
                    )}
                </div>
            </Modal.Body>

            <Modal.Footer>
                <Button variant="success" onClick={handleEditData}>{t(`ACCOUNT_INFORMATION.수정`, { defaultValue: '수정' })}</Button>
                <Button variant="secondary" onClick={handleClose}>{t(`ACCOUNT_INFORMATION.취소`, { defaultValue: '취소' })}</Button>
            </Modal.Footer>
        </Modal>
    );
}

export default Profile_001;